import { ArrowDown, ArrowUp } from 'lucide-react-native';
import { useCallback, useState } from 'react';
import { Pressable } from 'react-native';
import { FadeIn } from '../../../components/animations';
import { Box } from '../../../components/primitives/Box';
import { HStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useSourceLabel } from '../../../hooks/useSourceLabel';
import { colors } from '../../../theme/colors';
import type { SourcePerformanceItem } from '../hooks/useCampaignAnalytics';

interface SourcePerformanceTableProps {
  data: SourcePerformanceItem[];
}

type SortField = 'source' | 'leadCount' | 'wonCount' | 'conversionRate';
type SortDirection = 'asc' | 'desc';

function toNum(value: string | number): number {
  return typeof value === 'number' ? value : Number.parseFloat(String(value)) || 0;
}

// `key` is both the sort field and the column's label under
// `analytics.sourcePerformance.columns`, so a header cannot label the wrong
// column.
const COLUMNS: Array<{ key: SortField; flex: number }> = [
  { key: 'source', flex: 2 },
  { key: 'leadCount', flex: 1 },
  { key: 'wonCount', flex: 1 },
  { key: 'conversionRate', flex: 1 },
];

export function SourcePerformanceTable({ data }: Readonly<SourcePerformanceTableProps>) {
  const { t } = useTranslation();
  const sourceLabel = useSourceLabel();
  const [sortField, setSortField] = useState<SortField>('leadCount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = useCallback((field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDirection('desc');
      return field;
    });
  }, []);

  const sortedData = [...data].sort((a, b) => {
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    if (sortField === 'source') return multiplier * a.source.localeCompare(b.source);
    return multiplier * (toNum(a[sortField]) - toNum(b[sortField]));
  });

  if (data.length === 0) {
    return (
      <Box alignItems="center" justifyContent="center" style={{ minHeight: 120 }}>
        <Text variant="body" color={colors.text.tertiary}>
          {t('analytics.sourcePerformance.empty')}
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <HStack
        spacing="sm"
        style={{
          paddingVertical: 10,
          paddingHorizontal: 12,
          borderBottomWidth: 1,
          borderBottomColor: colors.border.subtle,
        }}
      >
        {COLUMNS.map((col) => (
          <Pressable
            key={col.key}
            onPress={() => handleSort(col.key)}
            style={{ flex: col.flex }}
            accessibilityRole="button"
          >
            <HStack spacing="xs" align="center">
              <Text
                variant="caption"
                color={sortField === col.key ? colors.primary.neon : colors.text.tertiary}
                style={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  fontWeight: '600',
                  fontSize: 10,
                }}
              >
                {t(`analytics.sourcePerformance.columns.${col.key}`)}
              </Text>
              {sortField === col.key &&
                (sortDirection === 'asc' ? (
                  <ArrowUp size={10} color={colors.primary.neon} />
                ) : (
                  <ArrowDown size={10} color={colors.primary.neon} />
                ))}
            </HStack>
          </Pressable>
        ))}
      </HStack>

      {sortedData.map((item, index) => (
        <FadeIn key={item.source} delay={index * 40}>
          <HStack
            spacing="sm"
            align="center"
            style={{
              paddingVertical: 10,
              paddingHorizontal: 12,
              backgroundColor: index % 2 === 0 ? colors.surface.row : 'transparent',
              borderBottomWidth: 1,
              borderBottomColor: colors.border.subtle,
            }}
          >
            <Box style={{ flex: 2 }}>
              <Text
                variant="body"
                color={colors.text.primary}
                numberOfLines={1}
                style={{ fontWeight: '500' }}
              >
                {sourceLabel(item.source)}
              </Text>
            </Box>
            <Box style={{ flex: 1 }}>
              <Text variant="body" color={colors.text.secondary} numberOfLines={1}>
                {item.leadCount}
              </Text>
            </Box>
            <Box style={{ flex: 1 }}>
              <Text variant="body" color={colors.text.secondary} numberOfLines={1}>
                {item.wonCount}
              </Text>
            </Box>
            <Box style={{ flex: 1 }}>
              <Text variant="body" color={colors.text.secondary} numberOfLines={1}>
                {toNum(item.conversionRate).toFixed(1)}%
              </Text>
            </Box>
          </HStack>
        </FadeIn>
      ))}
    </Box>
  );
}
